/**
 * network/FeedShell — reusable composer + list + scope-switcher.
 * Used from Community "Feed" tab and the profile page's activity section.
 *
 * Props:
 *   scope:    'for-you' | 'following' | 'public' | 'user'   (default 'for-you')
 *   authorId: required when scope='user'
 *   showComposer: bool (default true)
 *   showScopeTabs: bool (default true)
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import PostComposer from './PostComposer';
import PostCard from './PostCard';
import CommunityAd from '../community/CommunityAd';
import { Button } from '../ui/button';
import { Loader2, Sparkles } from 'lucide-react';

const TABS = [
  { key: 'for-you',   label: 'For you' },
  { key: 'following', label: 'Following' },
  { key: 'public',    label: 'Explore' },
];

const FeedShell = ({
  scope: initialScope = 'for-you',
  authorId,
  showComposer = true,
  showScopeTabs = true,
}) => {
  const { API } = useContext(AuthContext);
  const [scope, setScope] = useState(initialScope);
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(async (nextCursor = null) => {
    const params = new URLSearchParams();
    params.set('scope', scope);
    if (scope === 'user' && authorId) params.set('author_id', authorId);
    if (nextCursor) params.set('before', nextCursor);
    const { data } = await axios.get(`${API}/feed/posts?${params.toString()}`);
    return data;
  }, [API, scope, authorId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPage()
      .then((d) => { if (!alive) return; setPosts(d.posts || []); setCursor(d.next_cursor); })
      .catch(() => toast.error('Could not load the feed'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [fetchPage]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await fetchPage(cursor);
      setPosts((p) => [...p, ...(d.posts || [])]);
      setCursor(d.next_cursor);
    } catch { toast.error('Could not load more'); }
    finally { setLoadingMore(false); }
  };

  const onCreated = (post) => setPosts((p) => [post, ...p]);
  const onDelete  = (id)   => setPosts((p) => p.filter((x) => x.id !== id));

  return (
    <div className="space-y-4" data-testid="feed-shell">
      {showComposer && <PostComposer onCreated={onCreated} />}

      {showScopeTabs && (
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1" data-testid="feed-scope-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setScope(t.key)}
              className={`text-[11px] font-bold uppercase tracking-widest px-3 h-7 rounded-full transition-colors ${
                scope === t.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`feed-scope-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-muted-foreground text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> loading posts…
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-muted-foreground/60 mb-2" />
          <div className="font-heading font-extrabold">Nothing here yet</div>
          <p className="text-sm text-muted-foreground mt-1">Be the first to share something with the community.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p, i) => (
            <React.Fragment key={p.id}>
              <PostCard post={p} onDelete={onDelete} />
              {/* Intermittent promoted cards — network-style in-feed ads.
                  One after the 3rd post, then every 6th post. Silently hides
                  when the backend has no eligible ad for the placement. */}
              {(i === 2 || (i > 2 && (i - 2) % 6 === 0)) && (
                <CommunityAd
                  placement="feed"
                  className="border-l-4 border-primary/60"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {cursor && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded-full font-bold"
            data-testid="feed-load-more"
          >
            {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            Load older posts
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeedShell;
